import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { AlertTriangle } from 'lucide-react';
import { api } from '../../services/api';
import { MultiSelect } from './FilterBar';
import { fmtMoney, fmtMoneyShort, fmtNumber } from './format';
import { card, chart, tbl } from '../../theme';

/**
 * YearOverYear — the same calendar month, one bar per year.
 *
 * ── Why an ordinal ramp and not the categorical palette ─────────────────────
 * Years are ORDERED, so hue would be the wrong channel: swapping 2025 and 2026
 * changes the meaning, which is the test for ordinal. One navy hue stepped
 * light -> dark therefore carries the order itself, and the newest year is
 * always the darkest bar no matter how many years are on screen. Validated as
 * an ordinal ramp against this app's white card surface:
 *     monotone L PASS · adjacent ΔL PASS · single hue PASS (10° spread)
 *     light-end contrast 2.13:1 PASS (>= 2:1 ordinal floor)
 * The lightest steps sit under 3:1, which obligates relief rather than being
 * dismissable — hence the month table below the chart, which is also just the
 * faster way to read an exact number.
 *
 * Four years is the ceiling: past that the groups get too thin to compare and
 * the ramp runs out of separable steps.
 */
const RAMP = chart.sequential;
const MAX_YEARS = 4;

// Assign from the DARK end so the newest year is always #1B2A4A — the reader
// learns "darkest = most recent" once and it survives adding or dropping years.
function rampFor(n) {
  if (n <= 1) return [RAMP[3]];
  if (n === 2) return [RAMP[1], RAMP[3]];
  if (n === 3) return [RAMP[0], RAMP[2], RAMP[3]];
  return RAMP;
}

// Past ~1000% the decimal is noise, and these values only arise when a year is
// partially loaded — so they get rounded rather than dressed up as precision.
const fmtDeltaPct = (v) => {
  if (v === null || v === undefined) return '—';
  const pct = v * 100;
  return `${v >= 0 ? '+' : ''}${pct.toFixed(Math.abs(pct) >= 1000 ? 0 : 1)}%`;
};

// A year that is only partially loaded produces a percentage that is entirely an
// artifact of the missing rows — 27 transactions against 10,502 reads as
// "+106,500%" and would be screenshotted straight into a leadership deck. When
// the baseline is this thin the comparison is suppressed rather than captioned.
const COVERAGE_FLOOR = 0.25;

const fmtDeltaMoney = (v) =>
  v === null || v === undefined ? '—' : `${v >= 0 ? '+' : '-'}${fmtMoney(Math.abs(v))}`;

// Spend is the subject, so "up" is not good news — these are plain ink tokens
// with a direction word, not the reserved status green/red.
const deltaColor = (v) => (v === null || v === undefined ? '#9CA3AF' : v > 0 ? '#B42318' : '#067647');

export default function YearOverYear({ filterParams }) {
  const [picked, setPicked] = useState([]);

  const { data, isFetching } = useQuery({
    // `picked` is part of the key so switching years refetches; when it is empty
    // the endpoint picks the two most recent years that actually have data.
    queryKey: ['expense-yoy', filterParams, picked],
    queryFn:  () => api.get('/expenses/yoy', {
      params: { ...filterParams, ...(picked.length ? { compare_years: picked.join(',') } : {}) },
    }),
    keepPreviousData: true,
  });

  const years  = data?.years || [];
  const colors = useMemo(() => rampFor(years.length), [years.length]);
  const colorOf = (y) => colors[years.indexOf(y)] || RAMP[3];

  // Flatten { values: { 2025: {net_amount} } } into the flat keys Recharts wants.
  const chartData = useMemo(() => (data?.months || []).map((m) => {
    const row = { label: m.label, month_num: m.month_num, delta_amount: m.delta_amount, delta_pct: m.delta_pct };
    for (const y of years) {
      row[y] = m.values[y]?.net_amount ?? 0;
      row[`${y}__n`] = m.values[y]?.transaction_count ?? 0;
    }
    return row;
  }), [data, years]);

  const cmp = data?.comparison;

  // Transaction counts over the SAME window the headline compares, so the check
  // matches what is actually on screen rather than the two full years.
  const coverage = useMemo(() => {
    if (!cmp) return null;
    const inWindow = chartData.filter((r) => r.month_num <= cmp.through_month);
    const prior = inWindow.reduce((a, r) => a + (r[`${cmp.prior_year}__n`] || 0), 0);
    const curr  = inWindow.reduce((a, r) => a + (r[`${cmp.current_year}__n`] || 0), 0);
    return { prior, curr, comparable: curr === 0 || prior >= curr * COVERAGE_FLOOR };
  }, [cmp, chartData]);

  const comparable = coverage ? coverage.comparable : true;

  if (!isFetching && !years.length) {
    return (
      <div style={s.card}>
        <div style={s.title}>Year over Year</div>
        <div style={s.empty}>No expense data available to compare.</div>
      </div>
    );
  }

  return (
    <div style={{ ...s.card, opacity: isFetching ? 0.6 : 1 }}>
      <div style={s.head}>
        <div>
          <div style={s.title}>Year over Year by Month</div>
          <div style={s.subtitle}>
            Same calendar month, one bar per year — darkest is most recent.
            {' '}Date filters do not apply to this chart (comparing years is the point);
            {' '}every other filter does.
          </div>
        </div>
        <MultiSelect label="Compare years" width={150}
          options={(data?.available_years || []).map((y) => ({ value: y.year, label: y.year, count: y.n }))}
          value={picked.length ? picked : years}
          placeholder="2 most recent"
          // Keep the LAST four picked rather than refusing the click — silently
          // dropping the year someone just chose is the more confusing failure.
          onChange={(v) => setPicked(v.slice(-MAX_YEARS))} />
      </div>

      {/* Headline: like-for-like, so a part year is never compared against a
          full one. The full-year figures stay visible underneath. */}
      {cmp && (
        <div style={s.tiles}>
          <div style={s.tile}>
            <div style={s.tileLabel}>{cmp.prior_year} · Jan–{cmp.through_month_label}</div>
            <div style={s.tileValue}>{fmtMoney(cmp.prior_same_period, { cents: false })}</div>
          </div>
          <div style={s.tile}>
            <div style={s.tileLabel}>{cmp.current_year} · Jan–{cmp.through_month_label}</div>
            <div style={s.tileValue}>{fmtMoney(cmp.current_same_period, { cents: false })}</div>
          </div>
          <div style={s.tile}>
            <div style={s.tileLabel}>Change, same period</div>
            <div style={{ ...s.tileValue, color: comparable ? deltaColor(cmp.same_period_delta) : '#6B7280' }}>
              {fmtDeltaMoney(cmp.same_period_delta)}
              {comparable
                ? <span style={s.tilePct}>{fmtDeltaPct(cmp.same_period_delta_pct)}</span>
                : <span style={{ ...s.tilePct, color: '#9CA3AF' }}>not comparable</span>}
            </div>
          </div>
          <div style={s.note}>
            Compared through <strong>{cmp.through_month_label} {cmp.current_year}</strong>, the last
            month with data. Full years: {cmp.prior_year} {fmtMoney(cmp.prior_full, { cents: false })}
            {' · '}{cmp.current_year} {fmtMoney(cmp.current_full, { cents: false })}.
          </div>
        </div>
      )}

      {!comparable && coverage && (
        <div style={s.warn}>
          <AlertTriangle size={14} color="#B54708" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong>{cmp.prior_year} is only partially loaded</strong> — {fmtNumber(coverage.prior)} transaction
            {coverage.prior !== 1 ? 's' : ''} against {fmtNumber(coverage.curr)} in {cmp.current_year} over the
            same months. Percentage changes are hidden here because they would measure the missing history, not
            a change in spend. The dollar amounts are still what was recorded.
          </div>
        </div>
      )}

      <ResponsiveContainer width="100%" height={300}>
        {/* barGap 2 keeps a surface-colored sliver between adjacent bars so two
            ramp steps never touch and read as one block. */}
        <BarChart data={chartData} margin={{ top: 4, right: 12, bottom: 4, left: 4 }}
                  barGap={2} barCategoryGap="18%">
          <CartesianGrid vertical={false} stroke={chart.grid} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: chart.axisText }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 10, fill: chart.axisText }}
                 axisLine={false} tickLine={false} width={56} />
          <Tooltip
            wrapperStyle={{ pointerEvents: 'none' }}
            isAnimationActive={false}
            cursor={{ fill: 'rgba(27,42,74,0.05)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0].payload;
              return (
                <div style={s.tip}>
                  <div style={s.tipName}>{row.label}</div>
                  {years.map((y) => (
                    <div key={y} style={s.tipRow}>
                      <span style={{ ...s.swatch, background: colorOf(y) }} />
                      <span style={s.tipLabel}>{y}</span>
                      <span style={s.tipAmt}>{fmtMoney(row[y])}</span>
                    </div>
                  ))}
                  {years.length >= 2 && (
                    <div style={s.tipTotal}>
                      <span>Change</span>
                      <span style={{ color: comparable ? deltaColor(row.delta_pct) : '#6B7280' }}>
                        {fmtDeltaMoney(row.delta_amount)}
                        {comparable && ` · ${fmtDeltaPct(row.delta_pct)}`}
                      </span>
                    </div>
                  )}
                </div>
              );
            }}
          />
          {/* One year is not a comparison — the title already names it, so the
              legend box would just be a label for a label. */}
          {years.length >= 2 && (
            <Legend iconType="circle" iconSize={7} wrapperStyle={{ paddingTop: 6 }}
                    formatter={(v) => <span style={{ fontSize: 10.5, color: '#6B7280' }}>{v}</span>} />
          )}
          {years.map((y) => (
            <Bar key={y} dataKey={y} fill={colorOf(y)} radius={[4, 4, 0, 0]} maxBarSize={26} />
          ))}
        </BarChart>
      </ResponsiveContainer>

      {/* The table is the required relief for the lighter ramp steps, and the
          only place the exact monthly numbers are readable without hovering. */}
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, textAlign: 'left' }}>Month</th>
              {years.map((y) => (
                <th key={y} style={s.th}>
                  <span style={{ ...s.swatch, background: colorOf(y), marginRight: 5 }} />{y}
                </th>
              ))}
              {years.length >= 2 && <th style={s.th}>Change</th>}
              {years.length >= 2 && <th style={s.th}>%</th>}
            </tr>
          </thead>
          <tbody>
            {chartData.map((row) => (
              <tr key={row.label}>
                <td style={{ ...s.td, textAlign: 'left', color: '#6B7280' }}>{row.label}</td>
                {years.map((y) => <td key={y} style={s.td}>{fmtMoney(row[y], { cents: false })}</td>)}
                {years.length >= 2 && (
                  <td style={s.td}>{fmtDeltaMoney(row.delta_amount)}</td>
                )}
                {years.length >= 2 && (
                  <td style={{ ...s.td, color: comparable ? deltaColor(row.delta_pct) : '#9CA3AF' }}>
                    {comparable ? fmtDeltaPct(row.delta_pct) : '—'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ ...s.tfoot, textAlign: 'left' }}>Total</td>
              {years.map((y) => (
                <td key={y} style={s.tfoot}>
                  {fmtMoney(data?.totals_by_year?.[y]?.net_amount, { cents: false })}
                </td>
              ))}
              {years.length >= 2 && <td style={s.tfoot} />}
              {years.length >= 2 && <td style={s.tfoot} />}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

const s = {
  card:     { ...card.sm, padding: '14px 16px 12px', transition: 'opacity 0.15s' },
  head:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '10px' },
  title:    { fontSize: '13px', fontWeight: 600, color: '#111827' },
  subtitle: { fontSize: '11px', color: '#9CA3AF', marginTop: '2px', maxWidth: '620px', lineHeight: 1.5 },
  empty:    { padding: '48px 8px', textAlign: 'center', fontSize: '12px', color: '#9CA3AF' },

  tiles:     { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '10px 28px', padding: '10px 2px 14px' },
  tile:      { minWidth: '150px' },
  tileLabel: { fontSize: '10.5px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.4px' },
  tileValue: { fontSize: '20px', fontWeight: 600, color: '#111827', marginTop: '3px', fontVariantNumeric: 'tabular-nums' },
  tilePct:   { fontSize: '12px', fontWeight: 500, marginLeft: '7px' },
  note:      { flex: '1 1 260px', fontSize: '11px', color: '#9CA3AF', lineHeight: 1.55, minWidth: '240px' },
  warn:      { display: 'flex', gap: '8px', alignItems: 'flex-start', background: '#FFFAEB', border: '1px solid #FEDF89', borderRadius: '8px', padding: '9px 11px', marginBottom: '12px', fontSize: '11.5px', color: '#93370D', lineHeight: 1.55 },

  tip:      { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '9px 11px', boxShadow: '0 4px 14px rgba(0,0,0,0.08)', minWidth: '210px' },
  tipName:  { fontSize: '11.5px', fontWeight: 600, color: '#111827', marginBottom: '5px' },
  tipRow:   { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', padding: '1px 0' },
  swatch:   { display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  tipLabel: { flex: 1, color: '#6B7280' },
  tipAmt:   { color: '#111827', fontWeight: 500, fontVariantNumeric: 'tabular-nums' },
  tipTotal: { display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '5px', paddingTop: '5px', borderTop: '1px solid #F3F4F6', fontSize: '11px', fontWeight: 600, color: '#111827' },

  tableWrap: { marginTop: '10px', overflowX: 'auto' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontVariantNumeric: 'tabular-nums' },
  th:        { ...tbl.th, ...tbl.thRSm, fontSize: '10.5px', color: '#6B7280', whiteSpace: 'nowrap' },
  td:        { ...tbl.td, ...tbl.tdRSm, color: '#111827', borderBottom: '1px solid #F9FAFB' },
  tfoot:     { textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#111827', borderTop: '1px solid #E5E7EB', whiteSpace: 'nowrap' },
};
